import { LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { SlhCoreButton } from "@slhuicore/button";
import { SlhLoader } from "@slhui/loader";
import { style } from "./assets/button.generated-style";
import * as ButtonConstants from "./button.constants";

/**
 * Allow users to take actions, and make choices, with a single tap.
 * @CustomElement slh-button
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-m-font-size - Font size.
 * @cssprop --slh-base-semi-bold-font-weight - Font weight.
 * @cssprop --slh-base-xxs-line-height - Line height.
 * @cssprop --slh-color-neutral - Primary button hover / focus font color. Primary focus color. Secondary button hover / focus font color. Tertiary button hover / focus font color. Ghost button hover / focus font color. Negative button font color.
 * @cssprop --slh-color-neutral-shade-00 - Ghost button default / focus background color.
 * @cssprop --slh-color-neutral-shade-10 - Ghost button hover background color.
 * @cssprop --slh-color-neutral-shade-20 - Secondary button background color. Disabled button background / border color.
 * @cssprop --slh-color-neutral-shade-40 - Ghost button outline color. Disabled button font color.
 * @cssprop --slh-color-neutral-shade-80 - Primary button font color. Secondary button font color. Tertiary button font color. Ghost button font color.
 * @cssprop --slh-color-neutral-shade-80-opacity-18 - Primary / secondary / ghost / negative button focus shadow color.
 * @cssprop --slh-color-primary-darken - Tertiary button hover / focus text underline color.
 * @cssprop --slh-color-primary-shade-40 - Primary button background color.
 * @cssprop --slh-color-primary-shade-60 - Primary button hover / focus background color. Primary button border color.
 * @cssprop --slh-color-error-shade-60 - Negative button background color.
 * @cssprop --slh-color-error-shade-80 - Negative button hover / focus background color.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 * ** slots
 * @slot default - Button label
 * @slot icon-left - Custom content on the left side of the button label.
 * @slot icon-right - Custom content on the right side of the button label.
 */
@customElement("slh-button")
export default class SlhButton extends SlhCoreButton(LitElement) {
  static override styles = style;

  // eslint-disable-next-line class-methods-use-this
  private createLoaderElement(): HTMLElement {
    const loaderElement = new SlhLoader();

    setTimeout(() => {
      loaderElement?.shadowRoot
        ?.querySelector("svg")
        ?.classList.add(this.getLoaderClass());
    });

    return loaderElement;
  }

  private getLoaderClass(): string {
    switch (true) {
      case this.disabled:
        return ButtonConstants.BUTTON_COLOR_DISABLED;
      case this.secondary:
        return ButtonConstants.BUTTON_COLOR_SECONDARY;
      case this.ghost:
        return ButtonConstants.BUTTON_COLOR_GHOST;
      case this.tertiary:
        return ButtonConstants.BUTTON_COLOR_TERTIARY;
      case this.negative:
        return ButtonConstants.BUTTON_COLOR_NEGATIVE;
      default:
        return ButtonConstants.BUTTON_COLOR_PRIMARY;
    }
  }
}
